/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, computed, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {Subscription} from 'rxjs';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferSignalStateWrite} from '../util/template-state';
import template from './top-domain-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {StatsApiService} from './stats-api.service';
import {Downloader} from '../../../components/util/downloader';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';

@Component({
  standalone: false,
  selector: 'adn-top-domain-table',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopDomainTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() parentType: string;
  @Input() dateSelectorId: string;

  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  parentParam: string;
  readonly loaded = signal(false);
  readonly displayLoaded = computed(() => this.loaded());
  private destroyed = false;
  readonly error = signal(false);
  readonly dataTotals = signal<any[] | null>(null);
  readonly targetingExpression = signal(false);
  fields: string[] = [];
  customFields: string[] = [];
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);

  downloadFields: string[];
  downloadColumnOrder: string[];
  csvTableTitle: string;

  private chartDateListener: () => void;
  private allFields: any[];
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;
  private statsSubscription?: Subscription;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType || changes.dateSelectorId || changes.object) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject || !this.object?.id) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.params = {
      id: this.object.id,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;
    this.allFields = chartOptions.targetingFields;

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    const applyChartDates = (data: any) => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
        return;
      }
      // Defer date bindings — lastDates / chartDates can land during parent CD
      // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
      deferSignalStateWrite(() => {
        if (this.destroyed) {
          return;
        }
        if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
          return;
        }

        this.startDate.set(data.startDate);
        this.endDate.set(data.endDate);

        this.loaded.set(false);
        this.dataTotals.set(null);
        this.error.set(false);
        this.targetingExpression.set(false);

        if (this.startDate() && this.endDate()) {
          this.params.startDate = this.startDate();
          this.params.endDate = this.endDate();
        }

        this.statsSubscription?.unsubscribe();
        this.statsSubscription = this.statsApi.getDomain(this.params).subscribe({next: (dataTotals: any) => {
          const fields = ['impressions', 'rendered', 'viewables', 'clicks'];
          const customFields = _.filter(_.keys(dataTotals.totals), (k: string) => {
            return fields.indexOf(k) < 0 && k !== 'visibles';
          });
          deferSignalStateWrite(() => {
            if (this.destroyed) {
              return;
            }
            this.dataTotals.set(dataTotals.chunks);
            this.fields = fields;
            this.customFields = customFields;
            this.loaded.set(true);
          });
        }, error: (err: any) => {
          this.adnLog.warn(err);
          deferSignalStateWrite(() => {
            if (this.destroyed) {
              return;
            }
            this.error.set(true);
            this.loaded.set(true);
          });
        }});
      });
    };

    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      applyChartDates(lastDates);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    this.statsSubscription?.unsubscribe();
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  sortUpdate(field: string): void {
    if (field === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field;
    this.sorting.asc = true;
  }

  sortedDataTotals(): any[] {
    const dataTotals = this.dataTotals();
    if (!dataTotals) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(dataTotals, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  saveXls(): void {
    this.updateValues();
    new Downloader().saveXls2(this.csvTableTitle, this.downloadFields, this.downloadColumnOrder, this.dataTotals() || [], this.allFields);
  }

  private updateValues(): void {
    this.downloadFields = this.fields.concat(this.customFields);
    this.downloadFields.unshift('Domain');
    this.downloadColumnOrder = this.fields.concat(this.customFields);
    this.downloadColumnOrder.unshift('domain');
    this.csvTableTitle = 'Domain Totals';
  }
}
